import { useState, useCallback, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import { layerGroups } from "../config/layers";

/** Extend bounds from GeoJSON polygon or line geometries (validated coordinates). */
function extendBoundsFromGeometry(bounds, geometry) {
  if (!geometry?.coordinates) return;

  const extendRing = (ring) => {
    for (const coord of ring) {
      if (Array.isArray(coord) && coord.length >= 2) {
        bounds.extend([coord[0], coord[1]]);
      }
    }
  };

  if (geometry.type === "LineString") {
    extendRing(geometry.coordinates);
    return;
  }

  if (geometry.type === "MultiLineString") {
    for (const line of geometry.coordinates) {
      extendRing(line);
    }
    return;
  }

  if (geometry.type === "Polygon") {
    for (const ring of geometry.coordinates) {
      extendRing(ring);
    }
    return;
  }

  if (geometry.type === "MultiPolygon") {
    for (const polygon of geometry.coordinates) {
      for (const ring of polygon) {
        extendRing(ring);
      }
    }
  }
}

/**
 * Custom hook for managing map controls and layer visibility
 */
const useMapControls = ({ mapRef, updateLayerData }) => {
  // Map state
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loadingCount, setLoadingCount] = useState(0);
  const [loadingLayerName, setLoadingLayerName] = useState("");
  const [shimmerAnimation] = useState(null);

  const isLayerLoading = loadingCount > 0;

  // Load base counties layer
  const loadBaseCountiesLayer = useCallback(async (map) => {
    try {
      const response = await fetch("/data/counties.geojson");
      if (!response.ok) {
        throw new Error("Failed to fetch base counties data");
      }
      const geojsonData = await response.json();

      // Add base counties source if it doesn't exist
      if (!map.getSource("base-counties")) {
        map.addSource("base-counties", {
          type: "geojson",
          data: geojsonData,
        });

        // Add line layer first
        map.addLayer({
          id: "base-counties-line",
          type: "line",
          source: "base-counties",
          layout: {
            visibility: "visible",
          },
          paint: {
            "line-color": "#607D8B", // Medium blue-gray for base lines
            "line-width": 0.5,
            "line-opacity": 0.8,
          },
        });

        // Add fill layer with enhanced shimmer effect
        map.addLayer({
          id: "base-counties-fill",
          type: "fill",
          source: "base-counties",
          layout: {
            visibility: "visible",
          },
          paint: {
            "fill-color": "#ECEFF1", // Very light blue-gray for base fill
            "fill-opacity": 0.3, // Increased opacity for better visibility
            "fill-pattern": "shimmer", // Add shimmer pattern
          },
        });

        // Create shimmer pattern
        const createShimmerPattern = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = 128;
          canvas.height = 128;

          // Create gradient
          const gradient = ctx.createLinearGradient(0, 0, 128, 128);
          gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
          gradient.addColorStop(0.3, "rgba(255, 255, 255, 0.4)");
          gradient.addColorStop(0.5, "rgba(255, 255, 255, 0.6)");
          gradient.addColorStop(0.7, "rgba(255, 255, 255, 0.4)");
          gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, 128, 128);

          return ctx.getImageData(0, 0, 128, 128);
        };

        // Add shimmer pattern to map
        if (!map.hasImage("shimmer")) {
          map.addImage("shimmer", createShimmerPattern());
        }

        // Calculate bounds
        const bounds = new mapboxgl.LngLatBounds();
        geojsonData.features.forEach((feature) => {
          extendBoundsFromGeometry(bounds, feature.geometry);
        });
      }
    } catch (error) {
      console.error("Error loading base counties layer:", error);
      // Fallback to default Texas view on error
      map.easeTo({
        center: [-99.5, 31.5], // Center of Texas
        zoom: 5.15,
      });
    }
  }, []);

  // Cleanup shimmer animation on unmount
  useEffect(() => {
    return () => {
      if (shimmerAnimation) {
        cancelAnimationFrame(shimmerAnimation);
      }
    };
  }, [shimmerAnimation]);

  // Handle map load
  const handleMapLoad = useCallback(
    (_e) => {
      setMapLoaded(true);
      const map = mapRef.current?.getMap();
      if (!map) return;

      if (map.isStyleLoaded()) {
        loadBaseCountiesLayer(map).then(() => {
          toggleLayer(layerGroups.counties.layers[0], true, true);
        });
      } else {
        map.once("load", () => {
          loadBaseCountiesLayer(map).then(() => {
            toggleLayer(layerGroups.counties.layers[0], true, true);
          });
        });
      }
      // mapRef is stable; toggleLayer is defined below and intentionally omitted to preserve load order
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadBaseCountiesLayer],
  );

  // Toggle layer visibility
  const toggleLayer = useCallback(
    async (layer, isChecked, onload) => {
      const map = mapRef.current?.getMap();
      if (!map) return;

      try {
        if (!onload) {
          setLoadingCount((prev) => prev + 1);
          setLoadingLayerName(layer.title);
        }

        try {
          // First, ensure base counties layers exist and are visible
          if (!map.getSource("base-counties")) {
            await loadBaseCountiesLayer(map);
          }

          // Make sure base counties layers are visible
          if (map.getLayer("base-counties-fill")) {
            map.setLayoutProperty(
              "base-counties-fill",
              "visibility",
              "visible",
            );
          }
          if (map.getLayer("base-counties-line")) {
            map.setLayoutProperty(
              "base-counties-line",
              "visibility",
              "visible",
            );
          }

          // Update the layer data (add or remove features)
          await updateLayerData(layer, isChecked);

          // Ensure overlay layers are on top of base layers
          const layers = [
            "dmqt-layer-boundaries-fill",
            "dmqt-layer-boundaries-lines",
            "dmqt-layer-boundaries-hit",
            "dmqt-layer-labels",
          ];
          layers.forEach((layerId) => {
            if (map.getLayer(layerId)) {
              map.setLayoutProperty(layerId, "visibility", "visible");
              // Move overlay layers to the top
              map.moveLayer(layerId);
            }
          });
        } finally {
          if (!onload) {
            setLoadingCount((prev) => {
              const newCount = Math.max(0, prev - 1);
              if (newCount === 0) {
                setLoadingLayerName("");
              }
              return newCount;
            });
          }
        }
      } catch (error) {
        console.error("Error toggling layer:", error);
        if (!onload) {
          setLoadingCount((prev) => {
            const newCount = Math.max(0, prev - 1);
            if (newCount === 0) {
              setLoadingLayerName("");
            }
            return newCount;
          });
        }
      }
    },
    [mapRef, updateLayerData, loadBaseCountiesLayer],
  );

  // Fit map to bounds of visible layers
  const fitMapToBounds = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map || !map.style || !map.isStyleLoaded()) return;

    try {
      // Get all visible layers
      const visibleLayers = [];

      // Check county layers
      const countySource = map.getSource("dmqt-layer-boundaries");
      if (countySource && countySource._data) {
        visibleLayers.push(countySource._data);
      }

      // If no visible layers, return
      if (visibleLayers.length === 0) return;

      // Calculate bounds from all visible layers
      const bounds = new mapboxgl.LngLatBounds();

      visibleLayers.forEach((layer) => {
        if (layer.type === "FeatureCollection") {
          layer.features.forEach((feature) => {
            extendBoundsFromGeometry(bounds, feature.geometry);
          });
        } else if (layer.type === "Feature") {
          extendBoundsFromGeometry(bounds, layer.geometry);
        }
      });

      // Fit map to bounds with padding
      map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        linear: false, // Use fly animation for smoother transition
        duration: 1000, // Animation duration in milliseconds
      });
    } catch (error) {
      console.error("Error fitting map to bounds:", error);
    }
  }, [mapRef]);

  // Reset map view
  const resetMapView = useCallback(() => {
    // Check if mapRef and mapLoaded are available
    if (!mapRef.current || !mapLoaded) {
      console.warn("Map not loaded yet, cannot reset view");
      return;
    }

    try {
      const map = mapRef.current.getMap();

      // Additional check to ensure map is valid
      if (!map) {
        console.warn("Map instance is null, cannot reset view");
        return;
      }

      // Reset to default view
      map.easeTo({
        center: [-99.5, 31.5], // Center of Texas
        zoom: 5.15,
      });
    } catch (error) {
      console.error("Error resetting map view:", error);
    }
  }, [mapRef, mapLoaded]);

  return {
    mapLoaded,
    isLayerLoading,
    loadingLayerName,
    handleMapLoad,
    toggleLayer,
    resetMapView,
    fitMapToBounds,
  };
};

export default useMapControls;
